import { callApi } from './api';
import Content from '@/models/content';
import { MediaTypes, Persona, SubscriptionPlans } from '@/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const contentsApiUrl = import.meta.env.VITE_API_URL_V1 + '/contents';

export const getContents = async () => {
    return callApi<Content[]>({
        url: `${contentsApiUrl}`,
        method: 'GET',
    });
};
export const useContents = () => {
    return useQuery({
        queryKey: ['contents'],
        queryFn: getContents,
    });
};

export const getContent = async (contentId: string) => {
    return callApi<Content>({
        url: `${contentsApiUrl}/${contentId}`,
        method: 'GET',
    });
};
export const useContent = (contentId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['content', contentId],
        queryFn: () => getContent(contentId),
        enabled: !!contentId && isOpen,
    });
};

export const createContent = async ({
    title,
    categoryId,
    description,
    body,
    completionCaption,
    imagePath,
    mediaPath,
    mediaType,
    subscriptionPlan,
    personas,
    introSeconds,
}: {
    title: string;
    categoryId: string;
    description: string;
    body: string;
    completionCaption?: string;
    imagePath: string;
    mediaPath: string;
    mediaType: MediaTypes;
    subscriptionPlan: SubscriptionPlans;
    personas: Persona[];
    introSeconds?: number;
}) => {
    return callApi<Content>({
        url: `${contentsApiUrl}`,
        method: 'POST',
        data: { title, categoryId, description, body, imagePath, mediaPath, mediaType, subscriptionPlan, personas, completionCaption, introSeconds },
    });
};
export const useCreateContent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createContent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contents'] });
        },
    });
};

export const updateContent = async ({
    contentId,
    title,
    categoryId,
    description,
    body,
    imagePath,
    mediaPath,
    mediaType,
    subscriptionPlan,
    personas,
    completionCaption,
    introSeconds,
}: {
    contentId: string;
    title: string;
    categoryId: string;
    description: string;
    body: string;
    completionCaption?: string;
    imagePath: string;
    mediaPath: string;
    mediaType: MediaTypes;
    subscriptionPlan: SubscriptionPlans;
    personas: Persona[];
    introSeconds?: number;
}) => {
    return callApi({
        url: `${contentsApiUrl}/${contentId}`,
        method: 'PUT',
        data: { title, categoryId, description, body, imagePath, mediaPath, mediaType, subscriptionPlan, personas, completionCaption, introSeconds },
    });
};
export const useUpdateContent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateContent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contents'] });
        },
    });
};

const telegramBotApiUrl = import.meta.env.VITE_API_URL_V1 + '/telegram-bot';

export const broadcastDailyAffirmationVideo = async ({
    videoBase64,
    fileName,
    contentId,
    contentTitle,
    imageUrl,
    mediaLink,
}: {
    videoBase64: string;
    fileName: string;
    contentId: string;
    contentTitle?: string;
    imageUrl?: string;
    mediaLink?: string;
}) => {
    return callApi({
        url: `${telegramBotApiUrl}/broadcast-daily-affirmation-video`,
        method: 'POST',
        data: { videoBase64, fileName, contentId, contentTitle, imageUrl, mediaLink },
    });
};

export const deleteContent = async ({ contentId }: { contentId: string }) => {
    return callApi({
        url: `${contentsApiUrl}/${contentId}`,
        method: 'DELETE',
    });
};
export const useDeleteContent = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteContent,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contents'] });
        },
    });
};
